import React, { useState } from 'react';
import 'leaflet/dist/leaflet.css';
import {
  CircleMarker,
  MapContainer,
  TileLayer,
  Tooltip,
  WMSTileLayer,
} from 'react-leaflet';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip as MuiTooltip,
  Typography,
} from '@mui/material';
import { Close, Map as MapIcon } from '@mui/icons-material';
import { ILegalTransactionRelationshipRealEstate } from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipRealEstate/ILegalTransactionRelationshipRealEstate';
import { fetchBorisData } from '../../hooks/reactQuerys/RealEstateQueries';

export interface IRelatedParcelMarker {
  lat: number;
  lng: number;
  label?: string;
  /** Markerfarbe (z. B. zur Einfärbung nach Eigentümer). Default rot. */
  color?: string;
}

interface IMapProps {
  lat: number;
  lng: number;
  label?: string;
  /** Verwandte Grundstücke (gleicher Eigentümer/Blatt). Pro Marker einfärbbar. */
  related?: IRelatedParcelMarker[];
  /** Center-Marker (blau) ausblenden — z. B. wenn alle Punkte über `related` kommen. */
  hideCenter?: boolean;
}

/** Reine Kartendarstellung (Brandenburg): TopPlus-Basis + ALKIS-Flurstücke. */
export const RealEstateMap: React.FC<IMapProps> = ({
  lat,
  lng,
  label,
  related,
  hideCenter,
}) => (
  <MapContainer
    center={[lat, lng]}
    zoom={17}
    scrollWheelZoom
    style={{ height: 460, width: '100%' }}
  >
    {/* Basiskarte: OpenStreetMap */}
    <TileLayer
      attribution='&copy; OpenStreetMap-Mitwirkende'
      url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
    />
    {/* Optionales Kataster-Overlay (ALKIS Brandenburg) — außerhalb BB transparent */}
    <WMSTileLayer
      url='https://isk.geobasis-bb.de/ows/alkis_wms'
      format='image/png'
      layers='adv_alkis_flurstuecke'
      version='1.3.0'
      transparent
      opacity={0.7}
    />

    {/* Verwandte/aufgelistete Grundstücke (pro Marker einfärbbar) */}
    {related?.map((m, i) => (
      <CircleMarker
        key={`rel-${i}-${m.lat}-${m.lng}`}
        center={[m.lat, m.lng]}
        radius={9}
        pathOptions={{
          color: m.color ?? '#d32f2f',
          fillColor: m.color ?? '#d32f2f',
          fillOpacity: 0.6,
        }}
      >
        {m.label && <Tooltip>{m.label}</Tooltip>}
      </CircleMarker>
    ))}

    {!hideCenter && (
      <CircleMarker
        center={[lat, lng]}
        radius={10}
        pathOptions={{
          color: '#1976d2',
          fillColor: '#1976d2',
          fillOpacity: 0.7,
        }}
      >
        {label && <Tooltip permanent>{label}</Tooltip>}
      </CircleMarker>
    )}
  </MapContainer>
);

const buildAddress = (re: ILegalTransactionRelationshipRealEstate): string => {
  const line1 = [re.Street, re.StreetNr].filter(Boolean).join(' ').trim();
  const line2 = [re.Postcode, re.City].filter(Boolean).join(' ').trim();
  return [line1, line2].filter(Boolean).join(', ').trim();
};

interface IButtonProps {
  realEstate: ILegalTransactionRelationshipRealEstate;
  related?: IRelatedParcelMarker[];
}

/**
 * Button „Auf Karte" + Dialog mit der Grundstücks-Karte. Koordinaten aus
 * Latitude/Longitude des Grundstücks; fehlen sie, wird einmalig per BORIS
 * (Adresse → WGS84) geocodiert (benötigt aktive /realestate/boris-Route).
 */
export const RealEstateMapButton: React.FC<IButtonProps> = ({
  realEstate,
  related,
}) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(
    realEstate.Latitude != null && realEstate.Longitude != null
      ? { lat: realEstate.Latitude, lng: realEstate.Longitude }
      : null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const address = buildAddress(realEstate);

  const handleOpen = async () => {
    setOpen(true);
    setError(null);
    if (coords) return;
    if (!address) {
      setError('Keine Koordinaten und keine Adresse hinterlegt.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetchBorisData(address);
      if (res.koordinaten?.lat != null && res.koordinaten?.lon != null) {
        setCoords({ lat: res.koordinaten.lat, lng: res.koordinaten.lon });
      } else {
        setError(res.fehler ?? 'Koordinaten konnten nicht ermittelt werden.');
      }
    } catch {
      setError('Geocoding fehlgeschlagen.');
    } finally {
      setLoading(false);
    }
  };

  const labelParts = [address || `Grundstück #${realEstate.idRealEstate}`];

  return (
    <>
      <MuiTooltip title='Auf Karte anzeigen'>
        <Button
          size='small'
          variant='text'
          startIcon={<MapIcon fontSize='small' />}
          onClick={handleOpen}
        >
          Karte
        </Button>
      </MuiTooltip>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
          >
            <span>Lage &amp; Flurstück (Brandenburg)</span>
            <IconButton size='small' onClick={() => setOpen(false)}>
              <Close fontSize='small' />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Stack alignItems='center' sx={{ py: 6 }} spacing={1}>
              <CircularProgress />
              <Typography variant='body2' color='text.secondary'>
                Koordinaten werden ermittelt …
              </Typography>
            </Stack>
          ) : error ? (
            <Typography color='text.secondary' sx={{ py: 4 }}>
              {error}
            </Typography>
          ) : coords ? (
            <RealEstateMap
              lat={coords.lat}
              lng={coords.lng}
              label={labelParts.join(' · ')}
              related={related}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
};
